import PropTypes from "prop-types";
import React, { Component, Fragment } from "react";
import moment from "moment";
import { Popover, PopoverBody, TabContent, TabPane } from "reactstrap";
import TimePicker from "rc-time-picker";
import DayPicker from "react-day-picker";
import { observer, inject } from "mobx-react";
import classnames from "classnames";
import { Formik, Form } from "formik";

import {
  modalService,
  ProviderImage,
  Selector,
} from "../../../../components/index";
import providerStore from "../../../../services/providers/ProvidersStore";
import { PublishingDestinationSelector } from "../../PublishingDestinationSelector";
import {
  PublishingTeaserTooltip,
  showPublishingTeaserPopup,
} from "../PublishingTeaser";
import defaultUnapprovedPostsStore from "../../approvePosts/UnapprovedPostsStore";
import { canApprove } from "../postActions/ToolbarActions";
import {
  SOCIAL_POSTER_PERMISSION,
  SOCIAL_COMMENTER_PERMISSION,
  FACEBOOK,
  TWITTER,
  GOOGLE,
  LINKEDIN,
  INSTAGRAM,
} from "../../socialConstants";
import DefaultPost from "./forms/DefaultPost";
import { getFormName, getConfigByType } from "./forms/formConfigs";
import { getErrors } from "./ErrorHandling";
import { DefaultProvider, getDefaultPost } from "./PostToBePublished";
import { getPostTypeTabs, getTabComponent } from "./PostTopicType";
import { ProcessFailure, renderDate } from "../components/ScheduledPostCard";
import sessionStore from "../../../../services/session/SessionStore";
import selectedLocationStore from "../../../../services/locations/SelectedLocationsStore";

@observer
export default class PostEditor extends Component {
  static propTypes = {
    onComplete: PropTypes.func,
    post: PropTypes.object,
    preSavePost: PropTypes.func,
    isPublishingFeatureEnabled: PropTypes.bool,
    withoutOuterStyling: PropTypes.bool,
    mode: PropTypes.oneOf(["edit", "view"]),
  };

  static defaultProps = {
    preSavePost: () => {},
    mode: "edit",
  };

  isPublishingFeatureEnabled() {
    return (
      sessionStore.account &&
      sessionStore.account.services &&
      sessionStore.account.services.socialPublishing
    );
  }

  isInstagramPublishingEnabled() {
    return (
      sessionStore.account &&
      sessionStore.account.services &&
      sessionStore.account.services.sendiblePublishing
    );
  }

  constructor(params) {
    super(params);
    const post = params.post;

    const postProvider = !!post ? post.provider : DefaultProvider;
    const postTypeTabs = getPostTypeTabs(postProvider);
    const postTypeTab =
      postTypeTabs.length > 0
        ? !!post && post.postTopicType
          ? postTypeTabs.find((tab) => tab.key === post.postTopicType)
          : postTypeTabs[0] // Convention is first tab is default tab
        : undefined;

    this.state = {
      // TODO: Should we be overriding the userId when a different user saves it?
      userId: post ? post.userId : sessionStore.user.userId,
      showCalendar: false,
      existing: post,
      isSaving: false,
      isEditing: !!params.post,
      provider: postProvider,
      postTypeTab: postTypeTab,
      validatedPost: null,
    };
  }

  getAccountId(props = this.props) {
    return props.post ? props.post.accountId : sessionStore.account.accountId;
  }

  getBillingAccountId(props = this.props) {
    return props.post
      ? props.post.billingAccountId
      : sessionStore.account.billingAccountId;
  }

  handleSavePost = (validatedPost) => {
    this.savePost(validatedPost);
  };

  savePost = (post) => {
    this.props.preSavePost();
    if (!this.isPublishingFeatureEnabled) return;

    this.setState({ isSaving: true });
    let now = moment().tz(moment.tz.guess());

    const selectedEntity = selectedLocationStore.selectedEntity;
    const { onComplete } = this.props;
    let payload = {
      ...this.state.existing,
      provider: this.state.provider,
      publishType: "Post",
      publishTo: post.publishTo,
      userId: this.state.userId,
      accountId:
        selectedEntity && selectedEntity.primaryAccountId
          ? selectedEntity.primaryAccountId
          : this.getAccountId(),
      billingAccountId: this.getBillingAccountId(),
      rejectedForPublishing: false,
      scheduledTime: (!post.sendNow
        ? post.scheduledTime // keep the previous scheduled time if its due
        : this.state.existing &&
          moment(this.state.existing.scheduledTime).isBefore(now)
        ? moment(this.state.existing.scheduledTime)
        : now
      ).valueOf(),
      details: {
        text: post.text,
        immediateReply: post.immediateReply,
        attachments: post.attachments,
        googlePost: post.googlePost,
      },
      requiresApproval: !canApprove(),
      postTopicType:
        !!this.state.postTypeTab && this.state.postTypeTab.key !== "QA"
          ? this.state.postTypeTab.key
          : undefined,
    };

    const upserted = this.props.post
      ? defaultUnapprovedPostsStore.updatePost(payload)
      : defaultUnapprovedPostsStore.createPost(payload);

    upserted
      .then((p) => {
        onComplete(p);
      })
      .catch(onComplete);
  };

  renderTimeSummary = (post, formikProps) => {
    if (!post || this.props.mode === "view") return undefined;
    const { values } = formikProps;

    const Icon = ({ icon = "clock" }) => (
      <span
        className={`ss ss-clock mr-2`}
        style={{ fontSize: "18px", verticalAlign: "middle" }}
      />
    );

    let summaryContent;
    let alertCSS = "alert-warning";
    if (post.failure && post.failure.attemptsRemaining === 0) {
      alertCSS = "alert-danger";
      const { failures } = post.failure;
      const lastFailure = failures[failures.length - 1];
      summaryContent = (
        <span>
          <Icon icon="delete" /> Post failed! Was scheduled for{" "}
          {renderDate(post.scheduledTime)}
          {ProcessFailure(lastFailure)}
        </span>
      );
    } else if (post.countPosted === post.countQueued) {
      alertCSS = "alert-success";
      summaryContent = (
        <span>
          <Icon icon="check" /> Published at {renderDate(post.completeTime)}
        </span>
      );
    } else if (
      moment(values.scheduledTime).isSameOrBefore(
        moment().subtract(10, "minutes")
      )
    ) {
      summaryContent = (
        <Fragment>
          <Icon />
          Scheduled for {renderDate(values.scheduledTime)}{" "}
          <span className="font-weight-bold">(overdue)</span>
        </Fragment>
      );
    } else {
      summaryContent = (
        <Fragment>
          <Icon />
          Scheduled for {renderDate(values.scheduledTime)}
        </Fragment>
      );
    }

    return <div className={`alert ${alertCSS} m-0 mb-3`}>{summaryContent}</div>;
  };

  renderTopRow = (formikProps, config, onChangeSetObject) => {
    if (this.props.mode === "view") return undefined;
    const { values, handleReset } = formikProps;

    const accountId = this.getAccountId();
    // Instagram is a premium feature, therefore only paying customers should be able to see it
    const externalPublishingProviders = this.isInstagramPublishingEnabled()
      ? [INSTAGRAM]
      : [];

    return (
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
        <h4>{this.state.existing ? "Edit" : config.createDescription}</h4>
        <div className="d-flex flex-column flex-sm-row align-items-center">
          <span className="mr-2" style={{ minWidth: "150px" }}>
            <Selector
              required
              options={[FACEBOOK, TWITTER, GOOGLE, LINKEDIN].concat(
                externalPublishingProviders
              )}
              selected={this.state.provider}
              renderOption={(p) => (
                <span key={p}>
                  <ProviderImage
                    provider={p}
                    providers={providerStore.providers}
                  />
                  &nbsp;&nbsp;{p}
                </span>
              )}
              onSelected={(p) => {
                if (p !== values.provider) {
                  /* The validation relies on the config, and the config relies on the provider, thus this is state level. */
                  if (!this.state.isEditing) {
                    const tabs = getPostTypeTabs(p);
                    const tab = !!tabs && tabs.length ? tabs[0] : undefined;
                    this.setState(
                      { provider: p, postTypeTab: tab },
                      handleReset
                    );
                  }
                }
              }}
            />
          </span>
          <PublishingDestinationSelector
            multi={true}
            key={accountId}
            accountId={accountId}
            className="inline-select"
            provider={this.state.provider}
            selected={values.publishTo}
            onSelected={(opt) => {
              let selected;
              if (Array.isArray(opt)) {
                opt.forEach((option) => {
                  // We're doing some mutation somewhere and sometimes remoteIds is not present here
                  // So we need to add it from the singular remoteId field
                  if (!option.remoteIds && !!option.remoteId) {
                    option.remoteIds = [option.remoteId];
                  }
                });
              }

              if (!opt) {
                selected = [];
              } else if (Array.isArray(opt)) {
                selected = opt;
              } else {
                selected = [{ ...opt }];
              }
              onChangeSetObject({ publishTo: selected });
            }}
          />
        </div>
      </div>
    );
  };

  renderBottomRowButtons = (formikProps, onChangeSetObject) => {
    if (this.props.mode === "view") return undefined;

    const { errors, values, handleChange, touched } = formikProps;

    const hasPublishing = sessionStore.user.permissions.some((p) =>
      p.includes(SOCIAL_POSTER_PERMISSION, SOCIAL_COMMENTER_PERMISSION)
    );

    const canSubmit =
      hasPublishing ||
      sessionStore.user.permissions.indexOf("SubmitChatterPost") !== -1;
    const schedulePopoverTargetId = `showScheduleCalendar_${
      this.props.post ? "edit" : "create"
    }`;
    const hasApprove = canApprove();

    const isScheduledBeforeNow = values.scheduledTime.isSameOrBefore(moment());
    let submitText;
    if (canSubmit && !hasPublishing) {
      submitText = hasApprove ? "Submit Response" : "Submit for Approval";
    } else if (values.sendNow || isScheduledBeforeNow) {
      submitText = hasApprove ? "Publish Now" : "Submit for Approval";
    } else {
      submitText = hasApprove ? "Schedule" : "Submit for Approval";
    }
    const touchedArray = Object.keys(touched);
    let flattenedErrors = [];
    touchedArray.forEach((key) => {
      flattenedErrors = flattenedErrors.concat(errors[key] || []);
    });

    return (
      <div className="content-spacer-top d-flex flex-row-reverse justify-content-between">
        <div className="d-flex justify-content-end">
          {this.props.showCancelButton ? (
            <button
              className="btn btn-outline-secondary"
              onClick={() => modalService.closeModal()}
            >
              Cancel
            </button>
          ) : undefined}
          <div>
            <div className="input-group">
              <div className="input-group-append">
                <PublishingTeaserTooltip
                  hasPublishing={this.isPublishingFeatureEnabled()}
                >
                  <button
                    type="submit"
                    className="btn btn-secondary control-spacer-left rounded-input-left"
                    disabled={
                      this.state.isSaving || !this.isPublishingFeatureEnabled()
                    }
                  >
                    {submitText}
                  </button>
                  <button
                    id={schedulePopoverTargetId}
                    type={"button"}
                    className="btn btn-secondary button-primary-dropdown-append"
                    disabled={
                      this.state.isSaving || !this.isPublishingFeatureEnabled()
                    }
                    onClick={() =>
                      this.setState({ showCalendar: !this.state.showCalendar })
                    }
                  >
                    {this.state.isSaving ? (
                      <div
                        className={`pull-right fas fa-spinner fa-spin ${
                          this.state.isSaving ? "" : "d-none"
                        }`}
                      />
                    ) : (
                      <span className={`far fa-chevron-down`} />
                    )}
                  </button>
                </PublishingTeaserTooltip>
              </div>
            </div>
          </div>
          <Popover
            isOpen={this.state.showCalendar}
            target={schedulePopoverTargetId}
            toggle={() => {
              // click events on the time-popover within this popover trigger hides. Don't hide unless time-popover is closed.
              if (!manualTimeEdit) {
                this.setState({ showCalendar: false });
              }
            }}
          >
            <PopoverBody>
              <div>
                <label className="mr-2">
                  <input
                    type="radio"
                    checked={values.sendNow}
                    onChange={() => onChangeSetObject({ sendNow: true })}
                  />{" "}
                  Now
                </label>
                <label className="mr-2">
                  <input
                    type="radio"
                    checked={!values.sendNow}
                    onChange={() => onChangeSetObject({ sendNow: false })}
                  />{" "}
                  Later
                </label>
                <DateAndTimePicker
                  onClick={() =>
                    values.sendNow && onChangeSetObject({ sendNow: false })
                  }
                  date={moment(values.scheduledTime)}
                  minDate={moment()}
                  disabled={values.sendNow}
                  onChange={(t) => {
                    onChangeSetObject({ scheduledTime: t });
                  }}
                />
              </div>
            </PopoverBody>
          </Popover>
        </div>
      </div>
    );
  };

  renderForm = (formikProps) => {
    const {
      errors,
      values,
      handleChange,
      touched,
      handleReset,
      handleBlur,
    } = formikProps;
    const isPublishingEnabled = this.isPublishingFeatureEnabled();

    const formType = getFormName(this.state.provider, this.state.postTypeTab);
    const config = getConfigByType(formType);

    const onChangeSetObject = (changed) => {
      const key = Object.keys(changed)[0];
      handleChange({
        type: "any",
        target: {
          name: key,
          value: changed[key],
        },
      });
    };
    const touchedArray = Object.keys(touched);
    let flattenedErrors = [];
    touchedArray.forEach((key) => {
      flattenedErrors = flattenedErrors.concat(errors[key] || []);
    });

    // To match the [[PostEditorFormFields]]
    const formProps = {
      formType,
      config,
      provider: this.state.provider,
      showPublishingTeaser: !isPublishingEnabled,
      mode: this.props.mode,
      values,
      onChangeSetObject,
      onChange: handleChange,
      onBlur: handleBlur,
      touched: touchedArray,
      errors,
      openModal: this.props.openModal,
      closeModal: this.props.closeModal,
    };

    const tabs = getPostTypeTabs(this.state.provider);

    return (
      <Form>
        {this.renderTimeSummary(this.props.post, formikProps)}
        {this.renderTopRow(formikProps, config, onChangeSetObject)}
        <div className="card-spacer-top">
          {!!tabs && tabs.length ? (
            <nav className="nav nav-pills d-flex flex-column cursor-pointer">
              <div className="d-flex flex-row">
                {!this.state.isEditing &&
                  tabs.map((tab, index) => {
                    const linkId = `navItem${index}`;
                    const active =
                      !!this.state.postTypeTab &&
                      this.state.postTypeTab.key === `${tab.key}`;
                    return (
                      <li className={"nav-item"} key={`navitem${index}`}>
                        <a
                          className={`nav-item nav-link text-capitalize ${classnames(
                            {
                              active,
                            }
                          )}`}
                          id={linkId}
                          onClick={() => {
                            if (!isPublishingEnabled) {
                              showPublishingTeaserPopup();
                            } else {
                              // Reset the form so that the errors from one tab don't show up when viewing another tab/form.
                              formikProps.resetForm();
                              this.setState({ postTypeTab: tab }, handleReset);
                            }
                          }}
                        >
                          {tab.title}
                        </a>
                      </li>
                    );
                  })}
              </div>
              <TabContent
                className="mw-100 cursor-default"
                activeTab={this.state.postTypeTab.key}
              >
                <TabPane
                  className="card-spacer-bottom"
                  tabId={this.state.postTypeTab.key}
                >
                  {getTabComponent(this.state.postTypeTab, formProps)}
                </TabPane>
              </TabContent>
            </nav>
          ) : (
            <DefaultPost {...formProps} />
          )}
        </div>

        <div>
          {!!flattenedErrors && flattenedErrors.length ? (
            <div
              style={{ marginTop: "10px" }}
              className="alert alert-danger alert-dismissable"
            >
              Oops! Looks like your post isn't complete:
              <ul>
                {flattenedErrors.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            </div>
          ) : undefined}
        </div>
        {this.renderBottomRowButtons(formikProps, onChangeSetObject)}
      </Form>
    );
  };

  render() {
    const formType = getFormName(this.state.provider, this.state.postTypeTab);
    const config = getConfigByType(formType);
    const initialPost = getDefaultPost(this.props.post);

    return (
      <Formik
        onSubmit={this.handleSavePost}
        initialValues={initialPost}
        validate={getErrors(config, this.state.provider)}
        validateOnChange={false}
        validateOnBlur={true}
      >
        {(formikProps) => this.renderForm(formikProps)}
      </Formik>
    );
  }
}

let manualTimeEdit = false;
const DateAndTimePicker = ({ date, disabled, onChange, minDate, ...props }) => {
  return (
    <div {...props}>
      <div style={{ marginBottom: "5px" }}>
        <TimePicker
          className={disabled ? "disabled" : ""}
          value={date}
          onChange={(t) => onChange(t)}
          onClose={() => (manualTimeEdit = false)}
          onOpen={() => (manualTimeEdit = true)}
          allowEmpty={false}
          use12Hours={true}
          showSecond={false}
        />
      </div>
      <div className={disabled ? "disabled" : ""}>
        <DayPicker
          disabled={disabled}
          onDayClick={(t) => onChange(moment(t))}
          selectedDays={date && moment(date).toDate()}
          disabledDays={(date) =>
            !!disabled || (!!minDate && moment(date).isBefore(minDate))
          }
        />
      </div>
    </div>
  );
};
